import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PROJECTS, Project, ProjectCategory } from '../constants';
import { useLanguage } from '../lib/language';

// Text shadows for readability across dynamic backgrounds
const textShadow = '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)';
const textShadowLight = '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)';

// ============================================
// PROJECT CARD - FULL WIDTH VARIANT
// ============================================
interface ProjectCardProps {
  project: Project;
  index: number;
  layout: 'grid' | 'list';
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, index, layout }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { language } = useLanguage();

  if (layout === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link
          to="/works"
          className="group block py-8 md:py-10"
          style={{ borderBottom: '1px solid var(--theme-panel-border)' }}
        >
          <div className="grid grid-cols-12 gap-4 md:gap-8 items-center">
            {/* Number */}
            <div className="col-span-2 md:col-span-1">
              <span
                className="text-[11px] font-mono"
                style={{ color: 'var(--theme-text-muted)' }}
              >
                {project.id}
              </span>
            </div>

            {/* Image thumbnail */}
            <div className="col-span-4 md:col-span-2">
              <div
                className="relative aspect-[16/10] overflow-hidden"
                style={{ backgroundColor: 'var(--theme-panel-bg)' }}
              >
                <motion.img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  animate={{
                    scale: isHovered ? 1.1 : 1,
                    filter: isHovered ? 'grayscale(0%)' : 'grayscale(100%)'
                  }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />
                <motion.div
                  className="absolute inset-0 mix-blend-overlay"
                  animate={{ opacity: isHovered ? 0.4 : 0 }}
                  style={{ backgroundColor: project.color }}
                />
              </div>
            </div>

            {/* Title & subtitle */}
            <div className="col-span-6 md:col-span-5">
              <h3
                className="font-pixel text-xl md:text-2xl font-bold mb-1 transition-colors"
                style={{ color: isHovered ? project.color : 'var(--theme-text-primary)' }}
              >
                {project.title}
              </h3>
              <p style={{ color: 'var(--theme-text-muted)' }} className="text-sm mb-2">
                {language === 'en' ? project.subtitleEn : project.subtitle}
              </p>
              <p style={{ color: 'var(--theme-text-muted)' }} className="hidden md:block text-sm leading-relaxed max-w-xl opacity-80">
                {language === 'en' ? project.descriptionEn : project.description}
              </p>
            </div>

            {/* Category & year */}
            <div className="hidden md:block md:col-span-2">
              <span
                className="text-[11px] uppercase tracking-[0.2em]"
                style={{ color: 'var(--theme-text-muted)' }}
              >
                {project.category}
              </span>
            </div>

            {/* Year */}
            <div className="hidden md:block md:col-span-1">
              <span
                className="text-[11px] font-mono"
                style={{ color: 'var(--theme-text-muted)' }}
              >
                {project.year}
              </span>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex md:col-span-1 justify-end">
              <motion.span
                className="material-symbols-outlined"
                style={{ color: 'var(--theme-text-muted)' }}
                animate={{
                  x: isHovered ? 5 : 0,
                  color: isHovered ? project.color : 'var(--theme-text-muted)'
                }}
                transition={{ duration: 0.3 }}
              >
                arrow_forward
              </motion.span>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  // Grid layout
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group"
    >
      <Link to="/works" className="block">
        {/* Image */}
        <div
          className="relative aspect-[4/3] overflow-hidden mb-4"
          style={{ backgroundColor: 'var(--theme-panel-bg)' }}
        >
          <motion.img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
            animate={{
              scale: isHovered ? 1.05 : 1,
              filter: isHovered ? 'grayscale(0%)' : 'grayscale(100%)'
            }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Color accent */}
          <motion.div
            className="absolute inset-0 mix-blend-overlay pointer-events-none"
            animate={{ opacity: isHovered ? 0.3 : 0 }}
            transition={{ duration: 0.5 }}
            style={{ backgroundColor: project.color }}
          />

          {/* Corner indicator */}
          <motion.div
            className="absolute top-4 right-4 w-2 h-2"
            animate={{ backgroundColor: isHovered ? project.color : 'var(--theme-panel-border)' }}
            transition={{ duration: 0.3 }}
          />

          {/* View project button on hover */}
          <motion.div
            className="absolute bottom-4 left-4 right-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
            transition={{ duration: 0.3 }}
          >
            <span
              className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.15em] backdrop-blur-sm px-3 py-2"
              style={{
                backgroundColor: 'var(--theme-panel-bg)',
                color: 'var(--theme-text-primary)',
              }}
            >
              {language === 'es' ? 'Ver proyecto' : 'View Project'}
              <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </span>
          </motion.div>
        </div>

        {/* Content */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-mono"
              style={{ color: 'var(--theme-text-muted)' }}
            >
              {project.id}
            </span>
            <span
              className="text-[10px] uppercase tracking-[0.2em]"
              style={{ color: 'var(--theme-text-muted)' }}
            >
              {project.category}
            </span>
          </div>

          <h3
            className="font-pixel text-xl font-bold transition-colors"
            style={{ color: isHovered ? project.color : 'var(--theme-text-primary)' }}
          >
            {project.title}
          </h3>

          <p style={{ color: 'var(--theme-text-muted)' }} className="text-sm">
            {language === 'en' ? project.subtitleEn : project.subtitle}
          </p>

          <p style={{ color: 'var(--theme-text-muted)' }} className="text-sm leading-relaxed opacity-80">
            {language === 'en' ? project.descriptionEn : project.description}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 pt-2">
            {project.tags.map((tag) => (
              <span
                key={tag}
                className="text-[10px] font-mono"
                style={{ color: 'var(--theme-text-muted)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// ============================================
// WORKS PAGE
// ============================================
const Works: React.FC = () => {
  const { language } = useLanguage();
  const [activeFilter, setActiveFilter] = useState<ProjectCategory>('all');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');

  const filters: { label: string; value: ProjectCategory }[] = [
    { label: language === 'es' ? 'Todos' : 'All', value: 'all' },
    { label: language === 'es' ? 'Productos' : 'Products', value: 'product' },
    { label: 'Marketplaces', value: 'marketplace' },
    { label: language === 'es' ? 'Sitios web' : 'Websites', value: 'website' },
    { label: 'Landings', value: 'landing' },
    { label: 'AI', value: 'ai' },
  ];

  const filteredProjects = activeFilter === 'all'
    ? PROJECTS
    : PROJECTS.filter(p => p.category === activeFilter);

  return (
    <>
      <main className="min-h-screen pt-32 pb-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-16">
        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <span
            className="text-[11px] uppercase tracking-[0.3em] mb-4 block"
            style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
          >
            {language === 'es' ? 'Portafolio' : 'Portfolio'}
          </span>
          <h1
            className="text-5xl md:text-7xl font-black tracking-tight mb-6"
            style={{ color: 'var(--theme-text-primary)', textShadow }}
          >
            {language === 'es' ? 'Trabajo' : 'Work'}
          </h1>
          <p
            className="text-lg max-w-xl"
            style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
          >
            {language === 'es'
              ? 'Todos los proyectos visibles y separados por tipo: productos, marketplaces, sitios web, landings y experiencias con IA.'
              : 'All projects visible and separated by type: products, marketplaces, websites, landings, and AI experiences.'}
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12 pb-8"
          style={{ borderBottom: '1px solid var(--theme-panel-border)' }}
        >
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className="text-[11px] uppercase tracking-[0.15em] px-4 py-2 transition-all"
                style={{
                  color: activeFilter === filter.value
                    ? 'var(--theme-text-primary)'
                    : 'var(--theme-text-muted)',
                  backgroundColor: activeFilter === filter.value
                    ? 'var(--theme-btn-secondary-bg)'
                    : 'transparent',
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Layout toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLayout('grid')}
              className="p-2 transition-colors"
              style={{
                color: layout === 'grid'
                  ? 'var(--theme-text-primary)'
                  : 'var(--theme-text-muted)',
              }}
            >
              <span className="material-symbols-outlined text-xl">grid_view</span>
            </button>
            <button
              onClick={() => setLayout('list')}
              className="p-2 transition-colors"
              style={{
                color: layout === 'list'
                  ? 'var(--theme-text-primary)'
                  : 'var(--theme-text-muted)',
              }}
            >
              <span className="material-symbols-outlined text-xl">view_list</span>
            </button>
          </div>
        </motion.div>

        {/* Project count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8"
        >
          <span
            className="text-[11px] font-mono"
            style={{ color: 'var(--theme-text-muted)' }}
          >
            {language === 'es'
              ? `${filteredProjects.length} proyecto${filteredProjects.length !== 1 ? 's' : ''}`
              : `${filteredProjects.length} project${filteredProjects.length !== 1 ? 's' : ''}`}
          </span>
        </motion.div>

        {/* Projects */}
        <AnimatePresence mode="wait">
          {layout === 'grid' ? (
            <motion.div
              key={`grid-${activeFilter}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
            >
              {filteredProjects.map((project, idx) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={idx}
                  layout="grid"
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key={`list-${activeFilter}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-0"
            >
              {filteredProjects.map((project, idx) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={idx}
                  layout="list"
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty state */}
        {filteredProjects.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-24"
          >
            <p style={{ color: 'var(--theme-text-muted)' }}>
              {language === 'es' ? 'No se encontraron proyectos en esta categoría.' : 'No projects found in this category.'}
            </p>
          </motion.div>
        )}
        </div>
      </main>
    </>
  );
};

export default Works;

